import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Modal } from 'antd';
import Button from 'components/generalUI/Button';
import { useReactiveVar } from '@apollo/client';
import { errorData } from 'apollo/cache';
import { CustomError } from 'apollo/types';
import styles from './Error.module.scss';

interface ErrorModalProps {
  visible?: boolean;
  onClose: () => void;
}

const ErrorModal: FC<ErrorModalProps> = ({ visible, onClose }: ErrorModalProps) => {
  const { t } = useTranslation();

  const data = useReactiveVar(errorData);

  const renderErrorCodeText = (customError: CustomError) => {
    if (!customError.code || customError.code === 'NETWORK') {
      return t('errors.general.networkErrorLabel');
    }
    return customError.code;
  };

  const renderErrorMessageText = (customError: CustomError) => {
    if (!customError.code || customError.code === 'NETWORK') {
      return t('errors.general.networkErrorMessage');
    }
    return customError.message;
  };

  return (
    <>
      {data ? (
        <Modal
          centered
          visible={visible}
          onOk={onClose}
          onCancel={onClose}
          width="100%"
          className={styles.ErrorModal}
          footer={null}
        >
          <div className={styles.DecorationBar} />
          <Row justify="center" className={styles.ErrorContainer}>
            <Row justify="start" align="middle" className={styles.TitleContainer}>
              <Col
                xs={{ span: 4, offset: 10 }}
                md={{ span: 3, offset: 3 }}
                lg={{ span: 2, offset: 3 }}
                xl={{ span: 2, offset: 3 }}
              >
                <div className={styles.ErrorIcon}>!</div>
              </Col>
              <Col
                xs={{ span: 24 }}
                md={{ span: 14, offset: 1 }}
                lg={{ span: 16, offset: 0 }}
                xl={{ span: 15, offset: 1 }}
              >
                <Row className={styles.ModalTitle}>{t('errors.general.title')}</Row>
                <Row className={styles.ModalSubTitle}>{t('errors.general.subTitle')}</Row>
              </Col>
            </Row>
            <Row className={styles.ErrorInfo}>
              <Col style={{ width: '100%' }}>
                <Row justify="start" className={styles.ErrorMessageContainer}>
                  <Col xs={{ span: 7 }} md={{ span: 4, offset: 3 }}>
                    {t('errors.general.errorLabel')}
                  </Col>
                  <Col xs={{ span: 17 }} md={{ span: 14 }} className={styles.ErrorCode}>
                    {renderErrorCodeText(data)}
                  </Col>
                </Row>
                <Row justify="start" className={styles.ErrorMessageContainer}>
                  <Col xs={{ span: 7 }} md={{ span: 4, offset: 3 }}>
                    {t('errors.general.messageLabel')}
                  </Col>
                  <Col xs={{ span: 17 }} md={{ span: 14 }} className={styles.ErrorCode}>
                    {renderErrorMessageText(data)}
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row justify="start" className={styles.Contact}>
              <Col md={{ span: 18, offset: 3 }}>
                <Row justify="start" className={styles.ContactParagraph}>
                  <p>{t('errors.general.contactTextStart')}</p>
                </Row>
                <Row justify="start" className={styles.ContactParagraph}>
                  <p>
                    {t('errors.general.inLabel')}
                    <span className={styles.ContactBold}>
                      {t('errors.general.companyName')}
                    </span>
                    {t('errors.general.companyText')}
                    <a
                      href={`mailto:${t('errors.general.contactMail')}?subject=Error ${
                        data.code
                      }`}
                      className={styles.ContactBold}
                    >
                      {t('errors.general.contactMail')}
                    </a>
                    {t('errors.general.contactTextEnd')}
                  </p>
                </Row>
              </Col>
            </Row>
            <Row justify="center" className={styles.ButtonContainer}>
              <Button kind="secondary" onClick={onClose}>
                {t('errors.general.redirectText')}
              </Button>
            </Row>
          </Row>
        </Modal>
      ) : null}
    </>
  );
};

ErrorModal.defaultProps = {
  visible: false,
};

export default ErrorModal;
